import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, ArrowUpRight, Flame, Calendar, Users, MapPin, Hash, Heart, Eye, MapPinned } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SkeletonLoader } from './ui/SkeletonLoader';
import { useTrendingPosts, useTrendingEvents, useTrendingCreators, useLocationBasedTrending } from '../hooks/useTrending';
import { TrendingItem, TrendingCreator, LocationTrend } from '../services/trendingService';

// Creative category data with vibrant colors
const TRENDING_CATEGORIES = [
  {
    name: 'Art',
    tag: '#Art',
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-100 dark:bg-indigo-900/60',
    textColor: 'text-violet-700 dark:text-violet-300',
    borderColor: 'border-violet-300 dark:border-violet-500/40',
    glowColor: 'shadow-violet-500/20',
  },
  {
    name: 'Painting',
    tag: '#Painting',
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-100 dark:bg-amber-900/50',
    textColor: 'text-orange-700 dark:text-orange-300',
    borderColor: 'border-orange-300 dark:border-orange-500/40',
    glowColor: 'shadow-orange-500/20',
  },
  {
    name: 'Photography',
    tag: '#Photography',
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/50',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    borderColor: 'border-emerald-300 dark:border-emerald-500/40',
    glowColor: 'shadow-emerald-500/20',
  },
  {
    name: 'Doodle',
    tag: '#Doodle',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/50',
    textColor: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-300 dark:border-blue-500/40',
    glowColor: 'shadow-blue-500/20',
  },
  {
    name: 'Digital',
    tag: '#Digital',
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-100 dark:bg-pink-900/50',
    textColor: 'text-pink-700 dark:text-pink-300',
    borderColor: 'border-pink-300 dark:border-pink-500/40',
    glowColor: 'shadow-pink-500/20',
  },
  {
    name: 'Illustration',
    tag: '#Illustration',
    color: 'from-indigo-500 to-violet-500',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/60',
    textColor: 'text-indigo-700 dark:text-indigo-300',
    borderColor: 'border-indigo-300 dark:border-indigo-500/40',
    glowColor: 'shadow-indigo-500/20',
  },
  {
    name: '3D Art',
    tag: '#3DArt',
    color: 'from-cyan-500 to-sky-500',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/50',
    textColor: 'text-cyan-700 dark:text-cyan-300',
    borderColor: 'border-cyan-300 dark:border-cyan-500/40',
    glowColor: 'shadow-cyan-500/20',
  },
  {
    name: 'Sketch',
    tag: '#Sketch',
    color: 'from-amber-500 to-yellow-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/50',
    textColor: 'text-amber-700 dark:text-amber-300',
    borderColor: 'border-amber-300 dark:border-amber-500/40',
    glowColor: 'shadow-amber-500/20',
  },
];

type TabType = 'hashtags' | 'posts' | 'events' | 'creators' | 'nearby';

interface TrendingCategoryProps {
  category: typeof TRENDING_CATEGORIES[0];
  index: number;
  onClick: () => void;
}

const TrendingCategory: React.FC<TrendingCategoryProps> = ({ category, index, onClick }) => {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        type: 'spring',
        stiffness: 300,
        damping: 20
      }}
      whileHover={{
        scale: 1.08,
        transition: { duration: 0.3 }
      }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative overflow-hidden
        px-3 py-1.5 sm:px-4 sm:py-2
        rounded-full
        ${category.bgColor}
        ${category.textColor}
        ${category.borderColor}
        border
        backdrop-blur-sm
        font-medium text-xs sm:text-sm
        transition-all duration-300
        hover:shadow-lg ${category.glowColor}
        group
        cursor-pointer
      `}
    >
      {/* Animated gradient background on hover */}
      <div className={`
        absolute inset-0 
        bg-gradient-to-r ${category.color}
        opacity-0 
        group-hover:opacity-20 
        transition-opacity duration-300
      `} />

      {/* Shimmer effect */}
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <span className="relative z-10 flex items-center gap-1">
        {category.tag}
        <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </span>
    </motion.button>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
      transition-all duration-300
      ${active
        ? 'bg-violet-100 text-violet-700 border border-violet-300 dark:bg-violet-500/20 dark:text-violet-300 dark:border-violet-500/30'
        : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/30'
      }
    `}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const TrendingItemCard: React.FC<{
  item: TrendingItem;
  index: number;
  type: 'post' | 'event';
  onClick?: () => void;
}> = ({ item, index, type, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 dark:bg-slate-800/50 border border-neutral-200 dark:border-slate-700/30 hover:border-violet-300 dark:hover:border-violet-500/30 hover:bg-neutral-100 dark:hover:bg-slate-800/70 transition-all cursor-pointer group"
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center text-violet-600 dark:text-violet-400 font-bold text-sm">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-neutral-900 dark:text-slate-200 font-medium truncate group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">
        {item.title}
      </p>
      <p className="text-xs text-neutral-500 dark:text-slate-500">
        {type === 'post' ? 'Posted' : 'Event'} • {new Date(item.createdAt).toLocaleDateString()}
      </p>
    </div>
    <div className="flex items-center gap-1 text-rose-500 dark:text-rose-400 text-xs">
      <Heart className="w-3.5 h-3.5" />
      <span>{item.likes}</span>
    </div>
  </motion.div>
);

const CreatorCard: React.FC<{
  creator: TrendingCreator;
  index: number;
  onClick?: () => void;
}> = ({ creator, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 dark:bg-slate-800/50 border border-neutral-200 dark:border-slate-700/30 hover:border-violet-300 dark:hover:border-violet-500/30 hover:bg-neutral-100 dark:hover:bg-slate-800/70 transition-all cursor-pointer group"
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center text-amber-600 dark:text-amber-400 font-bold text-sm">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-neutral-900 dark:text-slate-200 font-medium truncate group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">
        {creator.name}
      </p>
      <p className="text-xs text-neutral-500 dark:text-slate-500">
        {creator.followers.toLocaleString()} followers
      </p>
    </div>
    <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
      <TrendingUp className="w-3.5 h-3.5" />
      <span>+{creator.growth}%</span>
    </div>
  </motion.div>
);

const LocationTrendCard: React.FC<{
  trend: LocationTrend;
  index: number;
  onClick?: () => void;
}> = ({ trend, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 dark:bg-slate-800/50 border border-neutral-200 dark:border-slate-700/30 hover:border-violet-300 dark:hover:border-violet-500/30 hover:bg-neutral-100 dark:hover:bg-slate-800/70 transition-all cursor-pointer group"
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-sm">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1">
        <MapPinned className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        <p className="text-sm text-neutral-900 dark:text-slate-200 font-medium truncate group-hover:text-violet-600 dark:group-hover:text-violet-300 transition-colors">
          {trend.location}
        </p>
      </div>
      <p className="text-xs text-neutral-500 dark:text-slate-500">
        {trend.postCount} posts trending here
      </p>
    </div>
    <div className="flex items-center gap-1 text-violet-600 dark:text-violet-400 text-xs">
      <Eye className="w-3.5 h-3.5" />
      <span>{trend.engagement.toLocaleString()}</span>
    </div>
  </motion.div>
);

interface TrendingSectionProps {
  className?: string;
  loading?: boolean;
  error?: Error | null;
  onHashtagClick?: (hashtag: string) => void;
  onPostClick?: (postId: string) => void;
  onEventClick?: (eventId: string) => void;
  onCreatorClick?: (creatorId: string) => void;
  onLocationClick?: (location: string) => void;
  userLocation?: string;
}

export const TrendingSection: React.FC<TrendingSectionProps> = ({
  className = '',
  loading: externalLoading = false,
  error: externalError = null,
  onHashtagClick,
  onPostClick,
  onEventClick,
  onCreatorClick,
  onLocationClick,
  userLocation,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('hashtags');

  // Fetch trending data
  const { posts, loading: postsLoading, error: postsError } = useTrendingPosts();
  const { events, loading: eventsLoading, error: eventsError } = useTrendingEvents();
  const { creators, loading: creatorsLoading, error: creatorsError } = useTrendingCreators();
  const { trends: locationTrends, loading: locationLoading, error: locationError } = useLocationBasedTrending(userLocation);
  // Safely get navigate - returns undefined if not in Router context (tests)
  let navigate: ReturnType<typeof useNavigate> | undefined;
  try {
    navigate = useNavigate();
  } catch {
    navigate = undefined;
  }

  // Check if we're in a Router context
  let isInRouter = false;
  try {
    useLocation();
    isInRouter = true;
  } catch {
    isInRouter = false;
  }

  const handleCategoryClick = (category: typeof TRENDING_CATEGORIES[0]) => {
    if (onHashtagClick) {
      onHashtagClick(category.tag);
    } else if (isInRouter && navigate) {
      const q = encodeURIComponent(category.name);
      navigate(`/explore?q=${q}&time=48h&tab=accounts`);
    }
  };

  const handlePostClick = (postId: string) => {
    if (onPostClick) {
      onPostClick(postId);
    } else if (isInRouter && navigate) {
      navigate(`/post/${postId}`);
    }
  };

  const handleEventClick = (eventId: string) => {
    if (onEventClick) {
      onEventClick(eventId);
    } else if (isInRouter && navigate) {
      navigate(`/events/${eventId}`);
    }
  };

  const handleCreatorClick = (creatorId: string) => {
    if (onCreatorClick) {
      onCreatorClick(creatorId);
    } else if (isInRouter && navigate) {
      navigate(`/profile/${creatorId}`);
    }
  };

  const handleLocationClick = (location: string) => {
    if (onLocationClick) {
      onLocationClick(location);
    } else if (isInRouter && navigate) {
      navigate(`/explore?location=${encodeURIComponent(location)}`);
    }
  };

  const getTabLoading = () => {
    switch (activeTab) {
      case 'posts': return postsLoading;
      case 'events': return eventsLoading;
      case 'creators': return creatorsLoading;
      case 'nearby': return locationLoading;
      default: return externalLoading;
    }
  };

  const getTabError = () => {
    switch (activeTab) {
      case 'posts': return postsError;
      case 'events': return eventsError;
      case 'creators': return creatorsError;
      case 'nearby': return locationError;
      default: return externalError;
    }
  };

  const isLoading = getTabLoading();
  const error = getTabError();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'hashtags':
        return (
          <div className="flex flex-wrap gap-2.5 sm:gap-3">
            {TRENDING_CATEGORIES.map((category, index) => (
              <TrendingCategory
                key={category.name}
                category={category}
                index={index}
                onClick={() => handleCategoryClick(category)}
              />
            ))}
          </div>
        );

      case 'posts':
        return posts.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            No trending posts from the last 48 hours
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {posts.map((post, index) => (
              <TrendingItemCard
                key={post.id}
                item={post}
                index={index}
                type="post"
                onClick={() => handlePostClick(post.id)}
              />
            ))}
          </div>
        );

      case 'events':
        return events.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            No trending events at the moment
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {events.map((event, index) => (
              <TrendingItemCard
                key={event.id}
                item={event}
                index={index}
                type="event"
                onClick={() => handleEventClick(event.id)}
              />
            ))}
          </div>
        );

      case 'creators':
        return creators.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            No rising creators found
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {creators.map((creator, index) => (
              <CreatorCard
                key={creator.id}
                creator={creator}
                index={index}
                onClick={() => handleCreatorClick(creator.id)}
              />
            ))}
          </div>
        );

      case 'nearby':
        return locationTrends.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            {userLocation
              ? `No trending content near ${userLocation}`
              : 'Enable location to see nearby trending content'}
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {locationTrends.map((trend, index) => (
              <LocationTrendCard
                key={trend.location}
                trend={trend}
                index={index}
                onClick={() => handleLocationClick(trend.location)}
              />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="p-4 sm:p-6 rounded-2xl bg-neutral-100 dark:from-slate-800/50 dark:to-slate-900/50 border border-neutral-200 dark:border-slate-700/50 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-full bg-neutral-300 dark:bg-slate-700/50 animate-pulse" />
            <div className="w-24 h-5 rounded-full bg-neutral-300 dark:bg-slate-700/50 animate-pulse" />
          </div>
          <SkeletonLoader lines={2} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="p-4 sm:p-6 rounded-2xl bg-red-50 dark:from-red-500/10 dark:to-red-600/10 border border-red-200 dark:border-red-500/20 backdrop-blur-sm">
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load trending categories. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`${className}`}
    >
      <div className="relative overflow-hidden p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-white via-neutral-50 to-violet-50/30 dark:bg-gradient-to-br dark:from-slate-900/90 dark:via-slate-950/90 dark:to-black border border-neutral-200 dark:border-slate-800/50 shadow-lg dark:shadow-xl backdrop-blur-xl">

        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-purple-500/5 dark:from-violet-500/10 dark:via-transparent dark:to-cyan-500/10 animate-pulse" />

        {/* Floating orbs for depth */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl" />

        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 3 }}
              className="p-2 rounded-xl bg-violet-100 dark:bg-violet-500/20 cursor-pointer hover:bg-violet-200 dark:hover:bg-violet-500/30 transition-colors"
              onClick={() => {
                if (isInRouter && navigate) {
                  navigate('/explore?time=48h');
                }
              }}
              title="Show trending posts from last 48 hours"
            >
              <TrendingUp className="w-5 h-5 sm:w-5 sm:h-5 text-violet-600 dark:text-violet-400" />
            </motion.div>
            <h2 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">
              Trending Now
            </h2>
          </div>

          {/* Description */}
          <p className="text-sm sm:text-base text-neutral-600 dark:text-slate-400 mb-4 leading-relaxed">
            {activeTab === 'hashtags' && (
              <>Don&apos;t miss these popular{' '}
                <span className="text-violet-600 dark:text-violet-400 font-semibold">artworks</span>
                {' '}that are gaining attention today!</>
            )}
            {activeTab === 'posts' && (
              <>Top posts from the last{' '}
                <span className="text-violet-600 dark:text-violet-400 font-semibold">48 hours</span>
                {' '}sorted by likes</>
            )}
            {activeTab === 'events' && (
              <>Trending{' '}
                <span className="text-violet-600 dark:text-violet-400 font-semibold">events</span>
                {' '}with highest engagement</>
            )}
            {activeTab === 'creators' && (
              <>Rising{' '}
                <span className="text-violet-600 dark:text-violet-400 font-semibold">creators</span>
                {' '}gaining momentum</>
            )}
            {activeTab === 'nearby' && (
              <>Trending content in{' '}
                <span className="text-violet-600 dark:text-violet-400 font-semibold">your area</span></>
            )}
          </p>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 pb-3 border-b border-neutral-200 dark:border-slate-700/50">
            <TabButton
              active={activeTab === 'hashtags'}
              onClick={() => setActiveTab('hashtags')}
              icon={<Hash className="w-4 h-4" />}
              label="Hashtags"
            />
            <TabButton
              active={activeTab === 'posts'}
              onClick={() => setActiveTab('posts')}
              icon={<Flame className="w-4 h-4" />}
              label="Posts"
            />
            <TabButton
              active={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
              icon={<Calendar className="w-4 h-4" />}
              label="Events"
            />
            <TabButton
              active={activeTab === 'creators'}
              onClick={() => setActiveTab('creators')}
              icon={<Users className="w-4 h-4" />}
              label="Creators"
            />
            <TabButton
              active={activeTab === 'nearby'}
              onClick={() => setActiveTab('nearby')}
              icon={<MapPin className="w-4 h-4" />}
              label="Nearby"
            />
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {renderTabContent()}
            </motion.div>
          </AnimatePresence>

          {/* Bottom glow line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />
        </div>
      </div>
    </motion.div>
  );
};

export default TrendingSection;
